import cx from "classnames";
import includes from "lodash/includes";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IonButton, IonIcon, IonInput, IonLabel } from "@ionic/react";
import { eyeOffOutline, eyeOutline } from "ionicons/icons";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";

import "./WLFormInput.scss";
import { UPPERCASE_FIELDS } from "../../../utils/common";
import get from "lodash/get";

// Get all props from IonInput then removed className and onIonChange to override
type OverriddentIonInputProps = Omit<
  React.ComponentProps<typeof IonInput>,
  "className" | "onIonChange" | "value"
>;

// see https://ionicframework.com/docs/api/input for the IonInput props
export interface WLFormInputProps extends Partial<OverriddentIonInputProps> {
  name: string;
  noMargin?: boolean;
  title?: string;
  className?: string;
  rules?: RegisterOptions;
  label?: string;
  allCaps?: boolean;
  darkMode?: boolean;
  customOnChange?: (value: string | number | null | undefined) => any;
  displayValue?: string | number | null;
}

/**
 * !Before using this component:
 * - Make sure parent is wrapped in <FormProvider></FormProvider>
 * - Make sure useForm methods is passed in FormProvider. Example:
 *  const useFormMethods = useForm({
 *   mode: "all",
 *   reValidateMode: "onChange",
 * });
 * <FormProvider {...useFormMethods}>
 *  <WLFormInput/>
 */

// Internal component for number inputs that uses hooks at the top level
interface NumberInputFieldProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  onBlur: () => void;
  name: string;
  label?: string;
  rules?: RegisterOptions;
  darkMode?: boolean;
  hidden?: boolean;
  noMargin?: boolean;
  className?: string;
  allCaps?: boolean;
  type?: string;
  showPassword: boolean;
  hasErrors: boolean;
  errorMsg?: string;
  customOnChange?: (value: string | number | null | undefined) => any;
  displayValue?: string | number | null;
  remainingProps: Omit<
    React.ComponentProps<typeof IonInput>,
    "className" | "onIonChange" | "value" | "type" | "onBlur" | "onFocus"
  >;
  children?: React.ReactNode;
}

const NumberInputField = ({
  value,
  onChange,
  onBlur,
  name,
  label,
  rules,
  darkMode,
  hidden,
  noMargin,
  className,
  allCaps,
  type,
  showPassword,
  hasErrors,
  errorMsg,
  customOnChange,
  displayValue,
  remainingProps,
  children,
}: NumberInputFieldProps) => {
  // For number inputs, keep string state to preserve intermediate values like "0.0"
  const [localStringValue, setLocalStringValue] = useState<string>(() => {
    if (value !== null && value !== undefined) {
      return String(value);
    }
    return "";
  });
  const isFocusedRef = useRef(false);
  const prevValueRef = useRef(value);

  // Sync local state when form value changes externally (not during typing)
  useEffect(() => {
    if (!isFocusedRef.current && value !== prevValueRef.current) {
      prevValueRef.current = value;
      if (value !== null && value !== undefined) {
        const stringVal = String(value);
        setLocalStringValue(stringVal);
      } else {
        setLocalStringValue("");
      }
    } else {
      // Always update prevValueRef to track changes, even when focused
      prevValueRef.current = value;
    }
  }, [value]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    const trimmed = localStringValue.trim();
    if (trimmed === "" || trimmed === "." || trimmed === "-") {
      onChange(null);
      setLocalStringValue("");
    } else {
      const numeric = Number(trimmed);
      if (!isNaN(numeric)) {
        onChange(numeric);
      } else {
        onChange(null);
        setLocalStringValue("");
      }
    }
    onBlur();
  }, [localStringValue, onChange, onBlur]);

  const handleInput = useCallback(
    (
      event: Parameters<
        NonNullable<React.ComponentProps<typeof IonInput>["onIonInput"]>
      >[0]
    ) => {
      const inputValue = event.detail.value;
      // Just update local string state - don't touch form value during typing
      setLocalStringValue(inputValue ?? "");
      // Call customOnChange with null for intermediate states
      if (customOnChange !== undefined) {
        const trimmed = (inputValue ?? "").trim();
        if (trimmed === "" || trimmed === "." || trimmed === "-") {
          customOnChange(null);
        } else {
          const numeric = Number(trimmed);
          customOnChange(!isNaN(numeric) ? numeric : null);
        }
      }
    },
    [customOnChange]
  );

  const finalDisplayValue =
    displayValue !== undefined ? displayValue : localStringValue;

  return (
    <div className={cx("wl-input-cta", !!hidden ? "ion-display-none" : "")}>
      {label && (
        <IonLabel
          className={cx(
            "input-label win-sub medium gilroy blue500 ion-text-start",
            rules !== undefined && rules.required !== undefined && "required",
            darkMode && "dark-mode"
          )}
        >
          {label}
        </IonLabel>
      )}

      <IonInput
        {...remainingProps}
        aria-labelledby={`${name}-label`}
        type={
          ((type === "password" && showPassword) || type === "email"
            ? "text"
            : type) as any
        }
        mode="md"
        onFocus={handleFocus}
        onIonFocus={handleFocus}
        onBlur={handleBlur}
        onIonBlur={handleBlur}
        className={`wl-form-input ${darkMode && "dark-mode"} ${
          includes(UPPERCASE_FIELDS, name) && "ion-text-capitalize"
        } ${hasErrors && "ion-invalid"} ${true && "ion-touched"} ${
          noMargin ? "no-margin" : ""
        } ${type} ${className !== undefined ? className : ""} ${
          rules !== undefined && rules.required !== undefined ? "required" : ""
        } ${allCaps && "all-caps"}`} // !workaround istouched in react-hook-form not working??
        {...(includes(UPPERCASE_FIELDS, name) && {
          autocapitalize: "words",
        })}
        value={finalDisplayValue}
        onIonInput={handleInput}
        fill="outline"
        errorText={hasErrors ? errorMsg || "Input invalid" : undefined}
      >
        {children}
      </IonInput>
      {type === "password" && (
        <IonButton
          fill="clear"
          className="password-helper-btn ion-no-padding ion-no-margin"
          color="dark"
        >
          <IonIcon icon={showPassword ? eyeOutline : eyeOffOutline} />
        </IonButton>
      )}
    </div>
  );
};

export const WLFormInput = (props: WLFormInputProps) => {
  const {
    name,
    noMargin,
    title,
    className,
    rules,
    label,
    type,
    allCaps,
    darkMode,
    children,
    hidden,
    customOnChange,
    errorText,
    ...remainingProps
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [showPassword, setShowPassword] = useState(false);

  // const hasErrors = !!errors && errors[name] && !isEmpty(errors[name]?.message);

  const errorMsg = get(errors, `${name}.message`, undefined) as
    | string
    | undefined;
  const hasErrors = !!errors && errorMsg !== undefined;
  const isNumberType = type === "number";

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur } }) => {
        if (isNumberType) {
          return (
            <NumberInputField
              value={value as number | null | undefined}
              onChange={onChange as (value: number | null) => void}
              onBlur={onBlur}
              name={name}
              label={label}
              rules={rules}
              darkMode={darkMode}
              hidden={hidden}
              noMargin={noMargin}
              className={className}
              allCaps={allCaps}
              type={type}
              showPassword={showPassword}
              hasErrors={hasErrors}
              errorMsg={errorMsg}
              customOnChange={customOnChange}
              displayValue={props.displayValue}
              remainingProps={remainingProps}
              children={children}
            />
          );
        }

        // Non-number inputs - render directly without hooks
        const handleInput = (
          event: Parameters<
            NonNullable<React.ComponentProps<typeof IonInput>["onIonInput"]>
          >[0]
        ) => {
          const inputValue = event.detail.value;
          onChange(inputValue);
          if (customOnChange !== undefined) {
            customOnChange(inputValue);
          }
        };

        const displayValue =
          props.displayValue !== undefined ? props.displayValue : value || "";

        return (
          <div
            className={cx("wl-input-cta", !!hidden ? "ion-display-none" : "")}
          >
            {label && (
              <IonLabel
                className={cx(
                  "input-label win-sub medium gilroy blue500 ion-text-start",
                  rules !== undefined &&
                    rules.required !== undefined &&
                    "required",
                  darkMode && "dark-mode"
                )}
              >
                {label}
              </IonLabel>
            )}

            <IonInput
              {...remainingProps}
              aria-labelledby={`${name}-label`}
              type={
                (type === "password" && showPassword) || type === "email"
                  ? "text"
                  : type
              }
              mode="md"
              onBlur={onBlur}
              onIonBlur={onBlur}
              className={`wl-form-input ${darkMode && "dark-mode"} ${
                includes(UPPERCASE_FIELDS, name) && "ion-text-capitalize"
              } ${hasErrors && "ion-invalid"} ${true && "ion-touched"} ${
                noMargin ? "no-margin" : ""
              } ${type} ${className !== undefined ? className : ""} ${
                rules !== undefined && rules.required !== undefined
                  ? "required"
                  : ""
              } ${allCaps && "all-caps"}`} // !workaround istouched in react-hook-form not working??
              {...(includes(UPPERCASE_FIELDS, name) && {
                autocapitalize: "words",
              })}
              value={displayValue}
              onIonInput={handleInput}
              fill="outline"
              errorText={hasErrors ? errorMsg || "Input invalid" : undefined}
            >
              {children}
            </IonInput>
            {type === "password" && (
              <IonButton
                fill="clear"
                className="password-helper-btn ion-no-padding ion-no-margin"
                color="dark"
                onClick={() => setShowPassword(!showPassword)}
              >
                <IonIcon icon={showPassword ? eyeOutline : eyeOffOutline} />
              </IonButton>
            )}
          </div>
        );
      }}
      rules={rules}
    />
  );
};
